import React, { useEffect, useMemo, useState } from "react";
import { createBrowserRouter, RouterProvider, Outlet, Link, useLocation } from "react-router-dom";
import { Image, Layout, Menu, MenuProps, Button, Popover, ConfigProvider } from "antd";
import {
	HomeTabKeyEnum,
	getLanguageList,
	getSeriesData,
	getTagsData,
	getUserData,
	getWholeData,
	titleObj,
} from "./components/utils";
import styled from "@emotion/styled";
import EpisodeComponent from "./components/episode";
import VideosComponent from "./components/videos";
import SeriesComponent from "./components/series";
import ZypeComponent from "./components/zype";
import WurlComponent from "./components/bookmarks";
import LanguageComponent from "./components/settings/language";
import Metadata from "./components/metadata";
import FileUploaderPageContent from "./components/import/videos";
import EpisodeScoring from "./components/episode_scoring";
import TagsComponent from "./components/tags";
import AutoPodderComponent from "./components/auto_podder";
import PreSeedingComponent from "./components/auto_podder/PreSeedingComponent";
import UploadPrograms from "./components/auto_podder/UploadPrograms";
import ProgramsListing from "./components/auto_podder/ProgramsListing";
import SegmentsListingComponent from "./components/scheduling/segments/index";

import BatchJobsComponent from "./components/batch_jobs";
import BatchJobsPoddedReviewComponent from "./components/batch_jobs/BatchJobsPoddedReviewComponent";
import BatchJobsCalendarComponent from "./components/batch_jobs/calendar_view";
import PostLogProcessorComponent from "./components/post_log_processor";
import EPGInputsUploadComponent from "./components/scheduling/epg_inputs_upload";
import { CaretDownOutlined, PoweroffOutlined } from "@ant-design/icons";
import DistributionPlatformComponent from "./components/distribution_partner";
import Captions from "./components/captions";
import SensitiveSearch from "./components/sensitive-search";
import { ConvertedDataType } from "./components/utils/common.interface";
import { SortObj } from "./components/videos/videos";
import {
	authAxios,
	getAPIToken,
	getEmailFromSessionStorage,
} from "./components/utils/session_utils";
import { imageUrls } from "./assets/images";
import CuePointsComponent from "./components/cue-points";
import ThumbnailsComponent from "./components/thumbnails";
import ViewershipUploadComponent from "./components/register/viewership_upload/index";
import PaymentManager from "./components/register/payment_manager/components/main";
import DealManager from "./components/register/deal_manager";
import BundleSpotDataUpload from "./components/salesforce/bundle_spot_data";
import AssetsExportComponent from "./components/export/assets";
import AssetsImport from "./components/import/assets";
import WurlExport from "./components/export/platforms/wurl";
import TitleConflictsComponent from "./components/asset-matching";
import VideoListsComponent from "./components/video-lists";
import { getVideoLists } from "./components/video-lists/utils";
import UserSettingsComponent from "./components/settings/user";
import PromosComponent from "./components/promos";
import SchedulerCalender from "./components/scheduling/scheduler/SchedulerCalender";
import CsvValidationComponent from "./components/scheduling/csv_validation";
import HorizonPdfUploader from "./components/salesforce/horizon_pdf_uploader";
import BTT_Uploader from "./components/salesforce/btt_uploader";
import AvailabilitySchedules from "./components/salesforce/availability_schedules";
import { fetchBookmarksFromDB } from "./components/bookmarks/utils";
import { Bookmark } from "./components/bookmarks/bookmark.types";
import BookmarksMenu from "./components/bookmarks/BookMarkDropDown";
import VideoTranscript from "./components/salesforce/Video_transcript";
import Analytics from "./components/analytics/Analytics";
import Validations from "./components/analytics/Validations";
import { themeConfig } from "./ThemeConfig";
import YoutubeExport from "./components/export/youtube";
import YouTubeLiveStreamList from "./components/youtube-live";
import PlayoutServerManager from "./components/youtube-live/PlayoutServerManager";
import ChannelManager from "./components/youtube-live/ChannelManager";
import YouTubeLiveConnections from "./components/youtube-live/YouTubeLiveConnections";
import BulkDescriptionsComponent from "./components/bulk-descriptions";
import axios from "axios";
import BundlePostLogUploader from "./components/salesforce/bundle_post_log_uploader";
import ProfanityCheck from "./components/profanity-gaurd";
import VideoComplianceUpload from "./components/video-compliance";
import AmagiIngest from "./components/amagi-ingest";
import TermsOfUse from "./components/legal/TermsOfUse";
import PrivacyPolicy from "./components/legal/PrivacyPolicy";
import Footer from "./components/legal/Footer";
import AutoPodderAdsAdjust from "./components/auto_podder/AutoPodderAdsAdjust";
import { MediaPlanBuilder } from "./components/media-plan-builder";
import { EstimateUploads } from "./components/estimate-uploads";
// import ZypeExport from "./components/export/platforms/zype";
import SyndicationReportExporter from "./components/auto_podder/SyndicationReportExporter";
import StationsListComponent from "./components/stations";
import AsPlayedReporting from "./components/youtube-live/AsPlayedReporting";
import TrafficInstructions from "./components/salesforce/traffic_instructions";
import OrderHighlighter from "./components/salesforce/order_highlighter";

// Type declaration for Google API
declare global {
	interface Window {
		google?: {
			accounts?: {
				id?: {
					initialize: (config: any) => void;
					renderButton: (element: HTMLElement, options: any) => void;
					disableAutoSelect: () => void;
					cancel: () => void;
				};
			};
			maps?: any; // Google Maps might be loaded from another source
		};
	}
}

const { Header, Content } = Layout;

const StyledHeader = styled(Header)`
	display: flex;
	padding: 0 12px;
	position: fixed;
	top: 0;
	left: 0;
	width: 100vw;
	z-index: 100;
	& .ant-menu {
		width: 100%;
		border-left: 1px solid #ccc;
		margin-left: 16px;
	}
`;

const StyledContent = styled(Content)`
	margin-top: 64px;
`;

const NotLoggedInOrDataDiv = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	flex: 1;
	background: #001529;
	color: #ddd;
	font-size: 24px;
	text-align: center;
	padding: 24px;

	.app-description {
		max-width: 700px;
		margin-top: 16px;
		font-size: 15px;
		color: rgba(255, 255, 255, 0.55);
		line-height: 1.7;
	}

	.app-description p {
		margin-bottom: 8px;
	}
`;

const MainLabelContainer = styled.div`
	span {
		margin-right: 4px;
	}
`;

function loadSessionStateByKey(key: string, defaultValue: any) {
	const item = window.sessionStorage.getItem(key);
	return item != null ? item : defaultValue;
}

export interface USERDATA {
	id: string;
	email: string;
	name: string;
	picture_url: string;
	email_verified: boolean;
	show_hidden_episodes: boolean;
	show_public_lists: boolean;
	wide_scrollbars: boolean;
	page_sizes: USER_PAGE_SIZE;
	hideandshowcolumns: HideAndShowColumns
}

export interface HideAndShowColumns {
  episodes: string[];
  videos: string[];
}

export interface USER_PAGE_SIZE {
	episodes: number;
	videos: number;
	tags: number;
	captions: number;
	series: number;
	distribution_platform: number;
	metadata: number;
}

export interface Ratings {
	au: string[];
	us: string[];
	canada: string[];
}

const loggedInNavigationMenu = (bookmarks: Bookmark[], handleLogout: () => void): MenuProps["items"] =>  [
		
	{
		key: "metadata-master",
		label: (
			<MainLabelContainer>
				<span>Metadata</span>
				<CaretDownOutlined />
			</MainLabelContainer>
		),
		children: [
			{
				key: HomeTabKeyEnum.EPISODE,
				label: <Link to={HomeTabKeyEnum.EPISODE}>Episodes</Link>,
			},
			{
				key: HomeTabKeyEnum.VIDEOS,
				label: <Link to={HomeTabKeyEnum.VIDEOS}>Videos</Link>,
			},
			{
				key: HomeTabKeyEnum.SERIES,
				label: <Link to={HomeTabKeyEnum.SERIES}>Series</Link>,
			},
			{
				key: HomeTabKeyEnum.METADATA,
				label: <Link to={HomeTabKeyEnum.METADATA}>Metadata</Link>,
			},
			{
				key: HomeTabKeyEnum.CAPTIONS,
				label: <Link to={HomeTabKeyEnum.CAPTIONS}>Captions</Link>,
			},
			{
				key: HomeTabKeyEnum.CUEPOINTS,
				label: <Link to={HomeTabKeyEnum.CUEPOINTS}>Cue Points</Link>,
			},
			{
				key: HomeTabKeyEnum.THUMBNAILS,
				label: <Link to={HomeTabKeyEnum.THUMBNAILS}>Thumbnails</Link>,
			},
			{
				key: HomeTabKeyEnum.PROMOS,
				label: <Link to={HomeTabKeyEnum.PROMOS}>Promos</Link>,
			},
			{
				key: HomeTabKeyEnum.DISTRIBUTIONR_PLATFORM,
				label: <Link to={HomeTabKeyEnum.DISTRIBUTIONR_PLATFORM}>Distribution Platforms</Link>,
			},
			{
				key: HomeTabKeyEnum.TAGS,
				label: <Link to={HomeTabKeyEnum.TAGS}>Tags</Link>,
			},
			{
				key: HomeTabKeyEnum.VIDEO_LISTS,
				label: <Link to={HomeTabKeyEnum.VIDEO_LISTS}>Video Lists</Link>,
			},
			{
				key: HomeTabKeyEnum.STATIONS,
				label: <Link to={HomeTabKeyEnum.STATIONS}>Stations</Link>,
			},
			{
				key: HomeTabKeyEnum.SENSITIVE_SEARCH,
				label: <Link to={HomeTabKeyEnum.SENSITIVE_SEARCH}>Sensitive Search</Link>,
			},
			{
				key: "settings",
				label: <Link to={HomeTabKeyEnum.SETTINGS_USER}>Settings</Link>,
				children: [
					{
						key: HomeTabKeyEnum.SETTINGS_USER,
						label: <Link to={HomeTabKeyEnum.SETTINGS_USER}>User</Link>,
					},
					{
						key: HomeTabKeyEnum.SETTINGS_LANGUAGE,
						label: <Link to={HomeTabKeyEnum.SETTINGS_LANGUAGE}>Language</Link>,
					},
				],
			},
			{
				key: "import",
				label: <Link to={HomeTabKeyEnum.VIDEOS_IMPORT}>Import</Link>,
				children: [
					{
						key: HomeTabKeyEnum.VIDEOS_IMPORT,
						label: <Link to={HomeTabKeyEnum.VIDEOS_IMPORT}>Video Import</Link>,
					},
					{
						key: HomeTabKeyEnum.ASSETS_IMPORT,
						label: <Link to={HomeTabKeyEnum.ASSETS_IMPORT}>Asset Import</Link>,
					},
				],
			},
			{
				key: "export",
				label: <Link to={HomeTabKeyEnum.ASSETS_EXPORT}>Asset Export</Link>,
			},
			// {
			// 	key: HomeTabKeyEnum.YOUTUBE_EXPORT	,
			// 	label: <Link to={HomeTabKeyEnum.YOUTUBE_EXPORT}>YouTube Export</Link>,
			// },
		],
	},
	{
		key: HomeTabKeyEnum.BOOKMARK,
		label: (
			<Popover
			content={<BookmarksMenu bookmarks={bookmarks} />}
			trigger="hover" 
			placement="bottom"
			>
			<span className="display-inline-block">Bookmarks &nbsp; <CaretDownOutlined /></span>
			</Popover>
		),
	},
	{
		key: "streaming-menu",
		label: (
			<MainLabelContainer>
				<span>Streaming</span>
				<CaretDownOutlined />
			</MainLabelContainer>
		),
		children: [
			{
				type: "group",
				label: "Scheduling",
				children: [
					{
						key: HomeTabKeyEnum.SCHEDULER,
						label: <Link to={HomeTabKeyEnum.SCHEDULER}>Scheduler</Link>,
					},
					{
						key: HomeTabKeyEnum.SEGMENTS_LISTING,
						label: <Link to={HomeTabKeyEnum.SEGMENTS_LISTING}>Segments Listing</Link>,
					},
					{
						key: HomeTabKeyEnum.CSV_VALIDATION,
						label: <Link to={HomeTabKeyEnum.CSV_VALIDATION}>CSV Validation</Link>,
					},
					{
						key: HomeTabKeyEnum.CREATE_EPG,
						label: <Link to={HomeTabKeyEnum.CREATE_EPG}>EPG from Amagi Schedules</Link>,
					},
					{
						key: HomeTabKeyEnum.BTT_UPLOADER,
						label: <Link to={HomeTabKeyEnum.BTT_UPLOADER}>BTT Uploader</Link>,
					},
					{
						key: HomeTabKeyEnum.EPISODE_SCORING,
						label: <Link to={HomeTabKeyEnum.EPISODE_SCORING}>Episode Scoring</Link>,
					},
				],
			},
			{
				type: "group",
				label: "Live Streams",
				children: [
					{
						key: HomeTabKeyEnum.YOUTUBE_LIVE_STREAMS,
						label: <Link to={HomeTabKeyEnum.YOUTUBE_LIVE_STREAMS}>Streams</Link>,
					},
					{
						key: HomeTabKeyEnum.PLAYOUT_SERVERS,
						label: <Link to={HomeTabKeyEnum.PLAYOUT_SERVERS}>Playout Servers</Link>,
					},
					{
						key: HomeTabKeyEnum.CHANNELS,
						label: <Link to={HomeTabKeyEnum.CHANNELS}>Channels</Link>,
					},
					{
						key: HomeTabKeyEnum.YOUTUBE_LIVE_CONNECTIONS,
						label: <Link to={HomeTabKeyEnum.YOUTUBE_LIVE_CONNECTIONS}>YouTube Connections</Link>,
					},
					{
						key: HomeTabKeyEnum.AS_PLAYED_REPORTING,
						label: <Link to={HomeTabKeyEnum.AS_PLAYED_REPORTING}>As Played Reporting</Link>
					}
				],
			},
		],
	},
	{
		key: "media-plan-builder-menu",
		label: (
			<MainLabelContainer>
				<span>Syndication</span>
				<CaretDownOutlined />
			</MainLabelContainer>
		),
		children: [
			{
				key: HomeTabKeyEnum.MEDIA_PLAN_BUILDER,
				label: <Link to={HomeTabKeyEnum.MEDIA_PLAN_BUILDER}>Media Plan Builder</Link>,
			},
			{
				key: HomeTabKeyEnum.ESTIMATE_UPLOADS,
				label: <Link to={HomeTabKeyEnum.ESTIMATE_UPLOADS}>Estimate Uploads</Link>,
			},
		],
	},
	{
		key: "auto-podder-menu",
		label: (
			<MainLabelContainer>
				<span>Auto Podder</span>
				<CaretDownOutlined />
			</MainLabelContainer>
		),
		children: [
			{
				key: HomeTabKeyEnum.AUTO_PODDER,
				label: <Link to={HomeTabKeyEnum.AUTO_PODDER}>AutoPodder</Link>,
			},
			{
                key: HomeTabKeyEnum.SYNDICATION_REPORT_EXPORTER,
                label: (
                    <Link to={HomeTabKeyEnum.SYNDICATION_REPORT_EXPORTER}>Syndication Report Exporter</Link>
                ),
            },
			{
				key: HomeTabKeyEnum.POST_LOG_PROCESSOR,
				label: (
					<Link to={HomeTabKeyEnum.POST_LOG_PROCESSOR}>Convert Post Log to Structure File </Link>
				),
			},
			{
				key: HomeTabKeyEnum.BATCH_JOBS,
				label: <Link to={HomeTabKeyEnum.BATCH_JOBS}>Batch Jobs</Link>,
			},
			{
				key: HomeTabKeyEnum.UPLOAD_PROGRAMS,
				label: <Link to={HomeTabKeyEnum.UPLOAD_PROGRAMS}>Upload Programs</Link>,
			},
			{
				key: HomeTabKeyEnum.PROGRAMS_LISTING,
				label: <Link to={HomeTabKeyEnum.PROGRAMS_LISTING}>Programs Listing</Link>,
			},
			{
				key: HomeTabKeyEnum.ADS_ADJUST,
				label: <Link to={HomeTabKeyEnum.ADS_ADJUST}>Ads Adjusting</Link>,
			},

		],
	},
	{
		key: "register-menu",
		label: (
			<MainLabelContainer>
				<span>Register</span>
				<CaretDownOutlined />
			</MainLabelContainer>
		),
		children: [
			{
				key: HomeTabKeyEnum.VIEWERSHIP_UPLOADER,
				label: <Link to={HomeTabKeyEnum.VIEWERSHIP_UPLOADER}>Viewership Uploader</Link>,
			},
			{
				key: HomeTabKeyEnum.PAYMENT_MANAGER,
				label: <Link to={HomeTabKeyEnum.PAYMENT_MANAGER}>Payment Manager</Link>,
			},
			{
				key: HomeTabKeyEnum.ASSET_MATCHING,
				label: <Link to={HomeTabKeyEnum.ASSET_MATCHING}>Asset Matching</Link>,
			},
			{
				key: HomeTabKeyEnum.DEAL_MANAGER,
				label: <Link to={HomeTabKeyEnum.DEAL_MANAGER}>Deal Manager</Link>,
			},
		],
	},
	{
		key: "salesforce-menu",
		label: (
			<MainLabelContainer>
				<span>Salesforce</span>
				<CaretDownOutlined />
			</MainLabelContainer>
		),
		children: [
			// {
			// 	key: HomeTabKeyEnum.BUNDLE_SPOT_DATA,
			// 	label: <Link to={HomeTabKeyEnum.BUNDLE_SPOT_DATA}>Bundle Spot Data</Link>,
			// },
			{
				key: HomeTabKeyEnum.HORIZON_PDF_UPLOADER,
				label: <Link to={HomeTabKeyEnum.HORIZON_PDF_UPLOADER}>Horizon PDF Parser</Link>,
			},
			{
				key: HomeTabKeyEnum.AVAILABILITY_SCHEDULES,
				label: <Link to={HomeTabKeyEnum.AVAILABILITY_SCHEDULES}>Availability Schedules</Link>,
			},
			{
				key: HomeTabKeyEnum.BUNDLE_POST_LOG_UPLOADER,
				label: <Link to={HomeTabKeyEnum.BUNDLE_POST_LOG_UPLOADER}>Post Log Uploader</Link>,
			},
			{
				key: HomeTabKeyEnum.TRAFFIC_INSTRUCTIONS,
				label: <Link to={HomeTabKeyEnum.TRAFFIC_INSTRUCTIONS}>Traffic Instructions</Link>,
			},
			{
				key: HomeTabKeyEnum.ORDER_HIGHLIGHTER,
				label: <Link to={HomeTabKeyEnum.ORDER_HIGHLIGHTER}>Order Highlighter</Link>,
			},
		]
	},
	{
		key: "tools-menu",
		label: (
			<MainLabelContainer>
				<span>Tools</span>
				<CaretDownOutlined />
			</MainLabelContainer>
		),
		children: [
			{
				key: 'analytics',
				label: 'Analytics',
				children: [
					{
						key: HomeTabKeyEnum.ANALYTICS,
						label: <Link to={HomeTabKeyEnum.ANALYTICS}>Analytics</Link>,
					},
					{
						key: HomeTabKeyEnum.VALIDATIONS,
						label: <Link to={HomeTabKeyEnum.VALIDATIONS}>Validations</Link>,
					},
				]
			},
			{
				key: "zype",
				label: "Zype",
				children: [
					{
						key: "zype-mrss-feed",
						label: (
							<a
								href={`${process.env.REACT_APP_BACK_END_API}/zype/live-feed`}
								target="_blank"
								rel="noreferrer"
							>
								Feed
							</a>
						),
					},
					{
						key: HomeTabKeyEnum.ZYPE,
						label: <Link to={HomeTabKeyEnum.ZYPE}>Zype Tools</Link>,
					},
				],
			},
			{
				key: HomeTabKeyEnum.VIDEO_TRANSCRIPT,
				label: <Link to={HomeTabKeyEnum.VIDEO_TRANSCRIPT}>Video Transcriber</Link>,
			},
			{
				key: HomeTabKeyEnum.BULK_DESCRIPTIONS,
				label: <Link to={HomeTabKeyEnum.BULK_DESCRIPTIONS}>Bulk Description Generator</Link>,
			},
			{
				key: HomeTabKeyEnum.PROFANITY_CHECK,
				label: <Link to={HomeTabKeyEnum.PROFANITY_CHECK}>Profanity Check</Link>,
			},
			{
				key: "video-compliance",
				label: <Link to={HomeTabKeyEnum.VIDEO_COMPLIANCE_UPLOAD}>Video Compliance</Link>,
			},
			{
				key: HomeTabKeyEnum.AMAGI_INGEST,
				label: <Link to={HomeTabKeyEnum.AMAGI_INGEST}>Amagi Ingest</Link>,
			},
		],
	},
	{
		key: "google-signin-button",
		// Pin the auth controls to the right edge of the horizontal (flex-row) menu.
		style: { marginLeft: "auto" },
		label: <div id="google-signin-div"></div>,
	},
	{
		key: "google-signin",
		label: (
			<div id="google-logout-div">
				<Button
					type="primary"
					icon={<PoweroffOutlined />}
					id="google-logout-button"
					onClick={handleLogout}
				>
					Logout
				</Button>
			</div>
		),
	},
];

const Root = () => {
	const location = useLocation();
	const [languagesList, setLanguagesList] = useState<any[]>([]);
	const [isLoggingIn, setIsLoggingIn] = useState<boolean>(false);
	const [user, setUser] = useState<USERDATA>();
	const [tagsList, setTagsList] = useState<any[]>([]);
	const [seriesList, setseriesList] = useState<any[]>([]);
	const [distributionPlatformData, setDistributionPlatformData] = useState<ConvertedDataType[]>([]);
	const [bookmarks, setBookmarks] = useState<Bookmark[]>([]);

	const [distributionPlatformSortObj] = useState<SortObj>({
		column: "name",
		order: "asc",
	});
	const [loading, setLoading] = useState<boolean>(false);
	const [currentUserEmail, setCurrentUserEmail] = useState<string>(
		loadSessionStateByKey("mvmgsid", "")
	);
	const [ratings, setRatings] = useState<Ratings>();
	const [videoLists, setVideoLists] = useState<any[]>([]);

	const [navigationMenu, setNavigationMenu] = useState([{
		key: "google-signin",
		label: <div id="google-signin-div"></div>,
	},])

	const [seriesSortObj] = useState<SortObj>({
		column: "updated_at",
		order: "desc",
	});

	// handle scrollbar styling change event
	useEffect(() => {
		const style = document.createElement("style");
		style.setAttribute("id", "custom-scrollbars");
		style.textContent = `
      ::-webkit-scrollbar {
        width: ${8}px;
        height: ${12}px;
      }
    `;
		if (user?.wide_scrollbars) document.head.appendChild(style);
		else {
			const style = document.getElementById("custom-scrollbars");
			if (style) document.head.removeChild(style);
		}

		return () => {
			const style = document.getElementById("custom-scrollbars");
			if (style) document.head.removeChild(style);
		};
	}, [user?.wide_scrollbars]);

	const getRatings = async () => {
		const response = await authAxios({
			method: "GET",
			url: `${process.env.REACT_APP_BACK_END_API}/snowflake/episode/ratings`,
		});
		setRatings(response.data);
	};

	const loadBookmarks = async () => {
		const data = await fetchBookmarksFromDB();
		if (Array.isArray(data) && data.length > 0) {
			setBookmarks(data);
		}
		return data;
	};
	
	const initialDataLoading = async (userEmail: string) => {
		const bookmarks = await loadBookmarks();
		getWholeData("distribution-platform", setDistributionPlatformData, distributionPlatformSortObj);
		getLanguageList(setLanguagesList);
		getSeriesData(setseriesList, (value: boolean) => value, seriesSortObj);
		getTagsData(setTagsList);
		getUserData(userEmail, setUser);
		getVideoLists(setVideoLists, null, {}, null, { showAllLists: true });
		getRatings();
		return bookmarks;
	};

	const handleLogout = async () => {
		try {
			// Call backend logout endpoint to clear auth cookies
			await axios.post(`${process.env.REACT_APP_BACK_END_API}/logout`, {}, {
				withCredentials: true
			});
		} catch (error) {
			console.log("Error during logout:", error);
		}

		// Call the appropriate logout function from the Google Identity Toolkit
		if (window.google && window.google.accounts && window.google.accounts.id) {
			window.google.accounts.id.disableAutoSelect();
			window.google.accounts.id.cancel();
		}
		// Handle any additional logout steps here
		window.sessionStorage.removeItem("mvmgsid");
		// clear session storage
		window.sessionStorage.clear();
		window.location.reload();

		setCurrentUserEmail("");
	}
	
	const checkCookieAuthentication = async () => {
		try {
		  setIsLoggingIn(true);
		  const response = await axios.get(`${process.env.REACT_APP_BACK_END_API}/check-auth`, {
			withCredentials: true
		  });
		  
		  if (response.data?.message === "Authenticated" && response.data?.userData) {
			const userData = response.data.userData;
			setUser(userData);
			setCurrentUserEmail(userData.email);
			window.sessionStorage.setItem("mvmgsid", userData.email);
			
			// Initialize app data
			const loadedBookmarks = await initialDataLoading(userData.email);
			const loggedInMenu = loggedInNavigationMenu(loadedBookmarks, handleLogout);
			setNavigationMenu(loggedInMenu as any);
			
			return true;
		  }
		} catch (error) {
		  console.log("No valid cookie authentication found");
		} finally {
		  setIsLoggingIn(false);
		}
		return false;
	};

	useEffect(() => {
		checkCookieAuthentication();
	}, []);

	const isPublicRoute = [HomeTabKeyEnum.TERMS_OF_USE, HomeTabKeyEnum.PRIVACY_POLICY].includes(location.pathname as HomeTabKeyEnum);

	useEffect(() => {
		if (location.pathname === "/" && currentUserEmail.length > 0) {
			window.location.pathname = HomeTabKeyEnum.EPISODE;
		}
		if (window) {
			document.title = location.pathname.toLowerCase().includes("episode")
				? "Episode - Nosey"
				: location.pathname.toLowerCase().includes("video") &&
				  !location.pathname.toLowerCase().includes("import")
				? "Video - Nosey"
				: `${titleObj[location.pathname]} - Nosey`;
		}

		const onOneTapSignedIn = async (response: any) => {
			setIsLoggingIn(true);
			const token = await getAPIToken(response.credential);
			window.sessionStorage.setItem("mvmtoken", token.token);
			const encodedEmail = token?.email;
			setCurrentUserEmail(encodedEmail);
			window.sessionStorage.setItem("mvmgsid", encodedEmail);

			// set user settings and user data
			setUser(token.user);
			// fetch distribution platform, series, language, tags data
			const loadedBookmarks = await initialDataLoading(encodedEmail);
			const loggedInMenu = loggedInNavigationMenu(loadedBookmarks, handleLogout);
			setNavigationMenu(loggedInMenu as any)
		};

		// console.log("Window google object:", window?.google);
		// console.log("Google accounts:", window?.google?.accounts);
		// console.log("Google accounts id:", window?.google?.accounts?.id);
		
		// Check for other Google services that might be loaded
		if (window?.google) {
			console.log("Available Google services:", Object.keys(window.google));
		}
		
		// Function to dynamically load Google Sign-In script
		const loadGoogleSignInScript = (): Promise<void> => {
			return new Promise((resolve, reject) => {
				// Check if script is already loaded
				if (window.google && window.google.accounts && window.google.accounts.id) {
					console.log("Google Sign-In API already available");
					resolve();
					return;
				}

				// Create and load the script
				console.log("Loading Google Sign-In script dynamically...");
				const script = document.createElement('script');
				script.id = 'gsi_script_tag';
				script.src = 'https://accounts.google.com/gsi/client';
				script.async = true;
				script.defer = true;
				
				script.onload = () => {
					console.log("Google Sign-In script loaded successfully, waiting for API to be ready...");
					// Wait for the API to be fully available
					const waitForAPI = setInterval(() => {
						if (window.google && window.google.accounts && window.google.accounts.id) {
							clearInterval(waitForAPI);
							console.log("Google Sign-In API is now ready");
							resolve();
						}
					}, 50); // Check every 50ms for faster response
					
					// Timeout after 5 seconds
					setTimeout(() => {
						clearInterval(waitForAPI);
						reject(new Error("Google Sign-In API failed to initialize after script load"));
					}, 5000);
				};
				
				script.onerror = () => {
					console.error("Failed to load Google Sign-In script");
					reject(new Error("Failed to load Google Sign-In script"));
				};
				
				document.head.appendChild(script);
			});
		};

		// Function to initialize Google Sign-In when API is available
		const initializeGoogleSignIn = () => {
			try {
				if (window.google && window.google.accounts && window.google.accounts.id) {
					console.log("Initializing Google Sign-In...");
					window.google.accounts.id.initialize({
						client_id: "667192520117-foq7du5o811hfir7ke8p95alral3dt0e.apps.googleusercontent.com",
						callback: onOneTapSignedIn,
						use_fedcm_for_prompt: true,
					});
					const button_div = document.getElementById("google-signin-div");
					console.log("🚀 ~ initializeGoogleSignIn ~ button_div:", button_div)
					// todo: check/sync if user is already logged in using data-skip_prompt_cookie cookie
					console.log("🚀 ~ initializeGoogleSignIn ~ currentUserEmail:", currentUserEmail)
					if (currentUserEmail.length === 0) {
						if (button_div) {
							window.google.accounts.id.renderButton(button_div, {
								theme: "outline",
								size: "large",
								text: "signin_with",
								shape: "rectangular",
							});
							console.log("Google Sign-In button rendered successfully");
						} else {
							console.warn("Google sign-in div not found");
						}
					}
				} else {
					console.log("Google API not fully available yet");
				}
			} catch (error) {
				console.error("Error initializing Google Sign-In:", error);
			}
		};

		// Main initialization logic
		const initializeGoogleAuth = async () => {
			try {
				console.log("Starting Google authentication initialization...");
				// Always load the script to ensure it's available
				await loadGoogleSignInScript();
				
				// Now initialize the Google Sign-In
				initializeGoogleSignIn();
			} catch (error) {
				console.error("Failed to initialize Google authentication:", error);
				console.warn("Google Sign-In will not be available");
			}
		};

		// Initialize Google authentication with a small delay to ensure DOM is ready
		setTimeout(() => {
			if(isLoggingIn) return; // if already logging in, don't initialize again
			initializeGoogleAuth();
		}, 100);

		// No cleanup needed for this approach
	}, [location.pathname, currentUserEmail]);

	useMemo(() => {
		const userEmail = getEmailFromSessionStorage();
		if (userEmail) {
			const loggedInMenu = loggedInNavigationMenu(bookmarks, handleLogout);
			setNavigationMenu(loggedInMenu as any)
			initialDataLoading(userEmail);
		}
	}, [currentUserEmail]);


	let styledContent = (
		<StyledContent>
			<Outlet
				context={{
					languagesList,
					tagsList,
					setTagsList,
					seriesList,
					setseriesList,
					loading,
					setLoading,
					distributionPlatformData,
					setDistributionPlatformData,
					distributionPlatformSortObj,
					user,
					setUser,
					ratings,
					videoLists,
					setVideoLists,
				}}
			/>
		</StyledContent>
	);

	if (currentUserEmail.length === 0 && !isPublicRoute) {
		// logged out
		styledContent = (
			<NotLoggedInOrDataDiv>
				<h1>
					{isLoggingIn
						? "Fetching your details. Please wait..."
						: "Please sign in to continue"
					}
				</h1>
				{!isLoggingIn && (
					<div className="app-description">
						<p>
							Nosey is an internal media management platform built by Marathon Ventures Media Sales.
							It enables authorized employees to manage media assets and operations.
						</p>
						<p>
							Sign-in is restricted to authorized Google Workspace accounts only.
						</p>
					</div>
				)}
			</NotLoggedInOrDataDiv>
		);
	}

	if (currentUserEmail.length && !user && !isPublicRoute) {
		styledContent = <NotLoggedInOrDataDiv>Please wait...</NotLoggedInOrDataDiv>;
	}

	return (
		<>
			<Layout style={{ minHeight: "100vh" }}>
				<StyledHeader>
					<Image width={150} preview={false} src={imageUrls.logo} />
					{
						!currentUserEmail.length && <div id=""></div>
					}
					<Menu
						theme="dark"
						mode="horizontal"
						items={navigationMenu}
						defaultSelectedKeys={["metadata-master", location.pathname]}
					/>
				</StyledHeader>
				<div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
					{styledContent}
				</div>
				<Footer />
			</Layout>
		</>
	);
};

const router = createBrowserRouter([
	{
		path: HomeTabKeyEnum.BASE,
		element: <Root />,
		errorElement: <NotLoggedInOrDataDiv>Page Not found</NotLoggedInOrDataDiv>,
		children: [
			{
				path: HomeTabKeyEnum.EPISODE,
				element: <EpisodeComponent />,
			},
			{
				path: HomeTabKeyEnum.EPISODE + "/:ref_id",
				element: <EpisodeComponent />,
			},
			{
				path: HomeTabKeyEnum.VIDEOS,
				element: <VideosComponent />,
			},
			{
				path: HomeTabKeyEnum.VIDEOS + "/:videoId",
				element: <VideosComponent />,
			},
			{
				path: HomeTabKeyEnum.METADATA,
				element: <Metadata />,
			},
			{
				path: HomeTabKeyEnum.SERIES,
				element: <SeriesComponent />,
			},
			{
				path: HomeTabKeyEnum.TAGS,
				element: <TagsComponent />,
			},
			{
				path: HomeTabKeyEnum.ZYPE,
				element: <ZypeComponent />,
			},
			{
				path: HomeTabKeyEnum.BOOKMARK,
				element: <WurlComponent />,
			},
			// {
			// 	path: HomeTabKeyEnum.WURL,
			// 	element: <WurlComponent />,
			// },
			{
				path: HomeTabKeyEnum.SETTINGS_LANGUAGE,
				element: <LanguageComponent />,
			},
			{
				path: HomeTabKeyEnum.SETTINGS_USER,
				element: <UserSettingsComponent />,
			},
			{
				path: HomeTabKeyEnum.ASSETS_EXPORT,
				element: <AssetsExportComponent />,
			},
			{
				path: HomeTabKeyEnum.VIDEOS_IMPORT,
				element: <FileUploaderPageContent />,
			},
			{
				path: HomeTabKeyEnum.ASSETS_IMPORT,
				element: <AssetsImport />,
			},
			{
				path: HomeTabKeyEnum.AUTO_PODDER_PRESEED,
				element: <PreSeedingComponent />,
			},
			{
				path: HomeTabKeyEnum.AUTO_PODDER,
				element: <AutoPodderComponent />,
			},
			{
				path: HomeTabKeyEnum.POST_LOG_PROCESSOR,
				element: <PostLogProcessorComponent />,
			},
			{
				path: HomeTabKeyEnum.BATCH_JOBS,
				element: <BatchJobsComponent />,
			},
			{
				path: HomeTabKeyEnum.BATCH_JOBS_PODDED_REVIEW,
				element: <BatchJobsPoddedReviewComponent />,
			},
			{
				path: HomeTabKeyEnum.BATCH_JOBS_CALENDAR,
				element: <BatchJobsCalendarComponent />,
			},
			{
				path: HomeTabKeyEnum.UPLOAD_PROGRAMS,
				element: <UploadPrograms />,
			},
			{
				path: HomeTabKeyEnum.PROGRAMS_LISTING,
				element: <ProgramsListing />,
			},
			{
				path: HomeTabKeyEnum.CREATE_EPG,
				element: <EPGInputsUploadComponent />,
			},
			{
				path: HomeTabKeyEnum.SCHEDULER,
				element: <SchedulerCalender />,
			},
			{
				path: HomeTabKeyEnum.SEGMENTS_LISTING,
				element: <SegmentsListingComponent />,
			},
			{
				path: HomeTabKeyEnum.CSV_VALIDATION,
				element: <CsvValidationComponent />,
			},
			{
				path: HomeTabKeyEnum.DISTRIBUTIONR_PLATFORM,
				element: <DistributionPlatformComponent />,
			},
			{
				path: HomeTabKeyEnum.CAPTIONS,
				element: <Captions />,
			},
			{
				path: HomeTabKeyEnum.CUEPOINTS,
				element: <CuePointsComponent />,
			},
			{
				path: HomeTabKeyEnum.THUMBNAILS,
				element: <ThumbnailsComponent />,
			},
			{
				path: HomeTabKeyEnum.PROMOS,
				element: <PromosComponent />,
			},
			{
				path: HomeTabKeyEnum.ASSET_MATCHING,
				element: <TitleConflictsComponent />,
			},
			{
				path: HomeTabKeyEnum.DEAL_MANAGER,
				element: <DealManager />,
			},
			{
				path: HomeTabKeyEnum.VIEWERSHIP_UPLOADER,
				element: <ViewershipUploadComponent />,
			},
			{
				path: HomeTabKeyEnum.PAYMENT_MANAGER,
				element: <PaymentManager />,
			},
			// {
			// 	path: HomeTabKeyEnum.ZYPE_EXPORT,
			// 	element: <ZypeExport />,
			// },
			{
				path: HomeTabKeyEnum.WURL_EXPORT,
				element: <WurlExport />,
			},
			{
				path: HomeTabKeyEnum.VIDEO_LISTS,
				element: <VideoListsComponent />,
			},
			{
				path: HomeTabKeyEnum.STATIONS,
				element: <StationsListComponent />,
			},
			{
				path: HomeTabKeyEnum.EPISODE_SCORING,
				element: <EpisodeScoring />,
			},
			{
				path: HomeTabKeyEnum.BUNDLE_SPOT_DATA,
				element: <BundleSpotDataUpload />,
			},
			{
				path: HomeTabKeyEnum.HORIZON_PDF_UPLOADER,
				element: <HorizonPdfUploader />,
			},
			{
				path: HomeTabKeyEnum.VIDEO_TRANSCRIPT,
				element: <VideoTranscript />,
			},
			{
				path: HomeTabKeyEnum.BTT_UPLOADER,
				element: <BTT_Uploader />,
			},
			{
				path: HomeTabKeyEnum.AVAILABILITY_SCHEDULES,
				element: <AvailabilitySchedules />,
			},
			{
				path: HomeTabKeyEnum.ANALYTICS,
				element: <Analytics />,
			},
			{
				path: HomeTabKeyEnum.VALIDATIONS,
				element: <Validations />,
			},
			{
				path: HomeTabKeyEnum.YOUTUBE_EXPORT,
				element: <YoutubeExport />,
			},
			{
				path: HomeTabKeyEnum.YOUTUBE_LIVE_STREAMS,
				element: <YouTubeLiveStreamList />,
			},
			{
				path: HomeTabKeyEnum.PLAYOUT_SERVERS,
				element: <PlayoutServerManager />,
			},
			{
				path: HomeTabKeyEnum.CHANNELS,
				element: <ChannelManager />,
			},
			{
				path: HomeTabKeyEnum.YOUTUBE_LIVE_CONNECTIONS,
				element: <YouTubeLiveConnections />,
			},
			{
				path: HomeTabKeyEnum.BULK_DESCRIPTIONS,
				element: <BulkDescriptionsComponent />,
			},
			{
				path: HomeTabKeyEnum.AS_PLAYED_REPORTING,
				element: <AsPlayedReporting />
			},
			{
				path: HomeTabKeyEnum.BUNDLE_POST_LOG_UPLOADER,
				element: <BundlePostLogUploader />,
			},
			{
				path: HomeTabKeyEnum.PROFANITY_CHECK,
				element: <ProfanityCheck />,
			},
			{
				path: HomeTabKeyEnum.VIDEO_COMPLIANCE_UPLOAD,
				element: <VideoComplianceUpload />,
			},
			{
				path: HomeTabKeyEnum.AMAGI_INGEST,
				element: <AmagiIngest />,
			},
			{
				path: HomeTabKeyEnum.TERMS_OF_USE,
				element: <TermsOfUse />,
			},
			{
				path: HomeTabKeyEnum.PRIVACY_POLICY,
				element: <PrivacyPolicy />,
			},
			{
				path: HomeTabKeyEnum.ADS_ADJUST,
				element: <AutoPodderAdsAdjust />,
			},
			{
				path: HomeTabKeyEnum.MEDIA_PLAN_BUILDER,
				element: <MediaPlanBuilder />,

			},
			{
				path: HomeTabKeyEnum.ESTIMATE_UPLOADS,
				element: <EstimateUploads />,
			},
			{
				path: HomeTabKeyEnum.SYNDICATION_REPORT_EXPORTER,
				element: <SyndicationReportExporter />,
			},
			{
				path: HomeTabKeyEnum.SENSITIVE_SEARCH,
				element: <SensitiveSearch />,
			},
			{
				path: HomeTabKeyEnum.TRAFFIC_INSTRUCTIONS,
				element: <TrafficInstructions />,
			},
			{
				path: HomeTabKeyEnum.ORDER_HIGHLIGHTER,
				element: <OrderHighlighter />,
			},
		],
	},
]);
const App = () => {
	return (
		<ConfigProvider theme={themeConfig}>
			<RouterProvider router={router} />
		</ConfigProvider>
	);
};

export default App;
